/**
 * Input validation utilities for security
 * Prevents injection attacks by validating user input before API calls
 */

// Safe search term pattern: alphanumeric, spaces, basic punctuation
const SEARCH_TERM_PATTERN = /^[a-zA-Z0-9\s\-_.@,]+$/;

// Stream name pattern: alphanumeric, hyphens, underscores
const STREAM_NAME_PATTERN = /^[a-zA-Z0-9\-_]+$/;

/**
 * Validates search term for safety
 * @param {string} searchTerm - Search term to validate
 * @param {number} maxLength - Maximum allowed length (default: 100)
 * @returns {string} Validated search term
 * @throws {Error} If format is invalid
 */
export const validateSearchTerm = (searchTerm, maxLength = 100) => {
  if (!searchTerm || typeof searchTerm !== 'string') {
    throw new Error('Search term is required');
  }

  const trimmed = searchTerm.trim();

  if (trimmed.length === 0) {
    throw new Error('Search term cannot be empty');
  }

  if (trimmed.length > maxLength) {
    throw new Error(`Search term cannot exceed ${maxLength} characters`);
  }

  if (!SEARCH_TERM_PATTERN.test(trimmed)) {
    throw new Error('Search term contains invalid characters');
  }

  return trimmed;
};

/**
 * Validates delivery stream name
 * @param {string} streamName - Stream name to validate
 * @returns {string} Validated stream name
 * @throws {Error} If format is invalid
 */
export const validateStreamName = (streamName) => {
  if (!streamName || typeof streamName !== 'string') {
    throw new Error('Stream name is required');
  }

  const trimmed = streamName.trim();

  if (!STREAM_NAME_PATTERN.test(trimmed)) {
    throw new Error('Invalid stream name format. Only alphanumeric characters, hyphens, and underscores allowed');
  }

  return trimmed;
};

/**
 * Sanitizes user input by encoding special characters
 * @param {string} input - Input to sanitize
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';

  return input
    .trim()
    .replace(/[<>"']/g, '') // Remove potentially dangerous characters
    .substring(0, 1000); // Limit length
};
